// Pembaruan (reader software updates; "gateway" in code): the page, its filters, the drawers, the rollout and every confirmation.
// Plain Indonesian for government admins: no developer jargon.
import type { OtaEventKind, OtaState } from '@lib/api/ota';

const v = (version: string) => `v${version.replace(/^v/, '')}`;
/** "v0.2.1" however the server spells the version. */
export const versionText = v;

export const UPDATES = {
  title: 'Pembaruan',
  sub: 'Pasang versi perangkat lunak terbaru ke reader secara bertahap.',
  tabsLabel: 'Isi halaman Pembaruan',
  tabGateways: 'Reader',
  tabReleases: 'Riwayat versi',
  tabGatewaysName: (n: string) => `Reader, ${n} reader`,
  tabReleasesName: (n: string) => `Riwayat versi, ${n} versi`,
} as const;

/** One label per server state (OtaState): badges, filters, the drawer. */
export const OTA_STATE_LABEL: Record<OtaState, string> = {
  none: 'Tidak ada pembaruan',
  queued: 'Menunggu dikirim',
  waiting_window: 'Menunggu jam pemeliharaan',
  offline: 'Menunggu online',
  sent: 'Perintah terkirim',
  in_progress: 'Sedang dipasang',
  installed: 'Terpasang',
  failed: 'Gagal, akan dicoba lagi',
  exhausted: 'Berhenti setelah gagal berulang',
  rolled_back: 'Kembali ke versi lama',
};
/** What each state means, for the drawer and the filter menu. */
export const OTA_STATE_HELP: Record<OtaState, string> = {
  none: 'Tidak ada versi yang dijadwalkan untuk reader ini.',
  queued: 'Perintah pasang dikirim pada pemeriksaan berikutnya.',
  waiting_window: 'Dipasang pada jam pemeliharaan berikutnya.',
  offline: 'Dipasang begitu reader tersambung lagi.',
  sent: 'Reader sudah menerima perintah dan belum melapor.',
  in_progress: 'Reader sedang mengunduh atau memasang versi baru.',
  installed: 'Reader sudah memakai versi tujuan.',
  failed: 'Pemasangan terakhir gagal; sistem mencoba lagi sampai batas percobaan.',
  exhausted: 'Sistem berhenti mencoba. Periksa reader, lalu ulangi.',
  rolled_back: 'Versi baru tidak berjalan normal, jadi reader kembali sendiri ke versi sebelumnya. Sistem tidak mencobanya lagi.',
};

/** The latest-version card. */
export const SUMMARY = {
  label: 'Versi terbaru',
  latest: 'Versi terbaru',
  released: (d: string) => `Dirilis ${d}`,
  more: 'Selengkapnya',
  moreName: (version: string) => `Selengkapnya tentang ${v(version)}`,
  noNotes: 'Tidak ada catatan untuk versi ini.',
  progressLabel: (version: string) => `Kemajuan pemasangan ${v(version)}`,
  progress: (installed: string, targeted: string) => `${installed} dari ${targeted} reader sudah memakai versi ini`,
  inUse: (running: string) => `Dipakai oleh ${running} reader`,
  notStarted: 'Belum ada pemasangan terjadwal. Gunakan Pasang versi untuk memasang versi ini ke reader lain.',
  percent: (p: number) => `${p}%`,
  install: 'Pasang versi',
  installName: (version: string) => `Pasang versi ${v(version)}`,
  retry: (n: string) => `Ulangi yang gagal (${n})`,
  retryName: (version: string, n: string) => `Ulangi pemasangan ${v(version)} yang gagal, ${n} reader`,
  cancelPending: (n: string) => `Batalkan yang menunggu (${n})`,
  cancelPendingName: (n: string) => `Batalkan ${n} pembaruan yang belum dimulai`,
  empty: { title: 'Belum ada versi', text: 'Versi baru muncul di sini setelah dirilis tim pengembang.' },
  failed: 'Ringkasan versi terbaru tidak dapat dimuat.',
} as const;

/** The legend under the progress bar (several states share a colour and a line). */
export const LEGEND = {
  installed: 'Terpasang',
  installing: 'Sedang dipasang',
  waiting: 'Menunggu jadwal',
  offline: 'Offline',
  failed: 'Gagal',
  rolledBack: 'Kembali ke versi lama',
  exhausted: (max?: number) => (max ? `Berhenti setelah ${max} kali gagal` : 'Berhenti setelah gagal berulang'),
  show: (label: string, n: string) => `Tampilkan ${n} reader: ${label}`,
} as const;

/** The reader tab (gateways in code): toolbar, table, selection. Copy says "reader" and "titik distribusi". */
export const OTA_LIST = {
  loading: 'Memuat reader…',
  noun: 'reader',
  columns: { gateway: 'Reader', region: 'Wilayah', condition: 'Kondisi', version: 'Versi terpasang', target: 'Tujuan', status: 'Status pembaruan', updated: 'Terakhir berubah' },
  online: 'Online',
  offline: 'Offline',
  notPlaced: 'Belum ditempatkan',
  noTarget: '–',
  attempts: (n: number) => `percobaan ke-${n}`,
  /** the failed badge: the attempt it is at, out of how many the server makes */
  failedAttempt: (n: number, max?: number) => (max ? `Gagal · percobaan ${n} dari ${max}` : `Gagal · percobaan ${n}`),
  empty: { title: 'Belum ada reader', text: 'Reader muncul di sini setelah tersambung ke sistem.' },
  noMatch: { title: 'Tidak ada reader yang cocok', text: 'Ubah kata kunci, versi, status, wilayah, atau kondisi.' },
  selectAllPage: 'Pilih semua reader di halaman ini',
  selectRow: (name: string) => `Pilih ${name}`,
  actionsColumn: 'Tindakan',
  menu: (name: string) => `Tindakan untuk ${name}`,
  actInstall: 'Pasang versi',
  actReinstall: 'Pasang ulang',
  actCancel: 'Batalkan pembaruan',
  actHistory: 'Riwayat pembaruan',
} as const;

export const OTA_FILTERS = {
  search: 'Cari reader',
  searchPlaceholder: 'Cari nama reader, kode, atau titik distribusi…',
  searchPlaceholderShort: 'Cari reader…',
  version: 'Versi',
  allVersions: 'Semua versi',
  versionsPicked: (n: number) => `${n} versi`,
  target: 'Versi tujuan',
  targetChip: (version: string) => `Tujuan ${version}`,
  noVersions: 'Belum ada versi.',
  status: 'Status pembaruan',
  statusShort: 'Status',
  allStatus: 'Semua status',
  statusPicked: (n: number) => `${n} status`,
  region: 'Wilayah',
  allRegions: 'Semua wilayah',
  province: 'Provinsi',
  anyProvince: 'Semua provinsi',
  provinceSearch: 'Cari provinsi…',
  noProvince: 'Tidak ada provinsi yang cocok.',
  regency: 'Kabupaten/kota',
  anyRegency: 'Semua kabupaten/kota',
  regencySearch: 'Cari kabupaten/kota…',
  noRegency: 'Tidak ada kabupaten/kota yang cocok.',
  regenciesFailed: 'Daftar kabupaten/kota gagal dimuat.',
  condition: 'Kondisi',
  allConditions: 'Semua kondisi',
} as const;

export const SELECTION = {
  label: 'Reader terpilih',
  count: (n: string) => `${n} dipilih`,
  allMatching: (n: string) => `Semua ${n} hasil filter dipilih`,
  selectAllMatching: (n: string) => `Pilih semua ${n} hasil filter`,
  install: 'Pasang versi',
  installShort: 'Pasang',
  cancel: 'Batalkan pembaruan',
  cancelShort: 'Batalkan',
  clear: 'Batalkan pilihan',
} as const;

/** The gateway drawer. */
export const GATEWAY_DRAWER = {
  label: (name: string) => `Pembaruan reader ${name}`,
  details: 'Rincian',
  code: 'Kode',
  location: 'Titik distribusi',
  region: 'Wilayah',
  condition: 'Kondisi',
  version: 'Versi terpasang',
  target: 'Versi tujuan',
  status: 'Status',
  attempts: 'Percobaan',
  attemptsValue: (n: number, max?: number) => (max ? `${n} dari ${max}` : String(n)),
  lastError: 'Kendala terakhir',
  updated: 'Terakhir berubah',
  open: 'Buka halaman reader',
  reinstallHelp: 'Sistem tidak akan mencoba lagi sendiri. Periksa reader dan sambungannya, lalu pilih Pasang ulang: percobaannya dimulai dari awal.',
  history: 'Riwayat pembaruan',
  historyNoun: 'langkah',
  historyEmpty: 'Belum ada riwayat pembaruan untuk reader ini.',
  historyFailed: 'Riwayat pembaruan tidak dapat dimuat.',
} as const;

export const OTA_EVENT_LABEL: Record<OtaEventKind, (version: string) => string> = {
  targeted: (x) => `Dijadwalkan memasang ${v(x)}`,
  sent: (x) => `Perintah pasang ${v(x)} dikirim`,
  reported_in_progress: (x) => `Mulai memasang ${v(x)}`,
  installed: (x) => `${v(x)} terpasang`,
  failed: (x) => `Gagal memasang ${v(x)}`,
  rolled_back: (x) => `Kembali ke versi lama setelah mencoba ${v(x)}`,
  cancelled: (x) => `Pembaruan ke ${v(x)} dibatalkan`,
  exhausted: (x) => `Berhenti mencoba ${v(x)}`,
};

/** The "Riwayat versi" tab and the release drawer. */
export const RELEASES = {
  loading: 'Memuat versi…',
  noun: 'versi',
  columns: { version: 'Versi', released: 'Dirilis', size: 'Ukuran', running: 'Dipakai', targeted: 'Dijadwalkan', notes: 'Catatan' },
  latest: 'Terbaru',
  gateways: (n: string) => `${n} reader`,
  menu: (version: string) => `Tindakan untuk ${v(version)}`,
  actInstall: 'Pasang versi ini',
  actView: 'Lihat rincian',
  actDelete: 'Hapus versi',
  drawerLabel: (version: string) => `Rincian versi ${v(version)}`,
  notes: 'Catatan versi',
  facts: 'Berkas',
  released: 'Dirilis',
  size: 'Ukuran',
  fingerprint: 'Sidik berkas (SHA-256)',
  fingerprintHelp: 'Reader memeriksa sidik ini sebelum memasang, sehingga berkas yang rusak atau diubah tidak akan terpasang.',
  copy: 'Salin',
  copyName: (version: string) => `Salin sidik berkas ${v(version)}`,
  copied: 'Sidik berkas disalin',
  copyFailed: 'Tidak bisa menyalin otomatis. Pilih teksnya lalu salin manual.',
  usage: 'Pemakaian',
  running: 'Sedang dipakai',
  targeted: 'Dijadwalkan',
  deleteBlocked: (n: string) => `Versi ini masih dijadwalkan untuk ${n} reader, jadi belum bisa dihapus. Batalkan pembaruannya dulu.`,
  failed: 'Rincian versi tidak dapat dimuat.',
  empty: SUMMARY.empty,
} as const;

/** The rollout drawer, step by step. */
export const ROLLOUT = {
  title: 'Pasang versi',
  label: 'Pasang versi ke reader',
  stepVersion: 'Versi',
  versionLatest: 'terbaru',
  versionOption: (version: string, date: string, latest: boolean) => `${v(version)} · ${date}${latest ? ' · terbaru' : ''}`,
  versionsFailed: 'Daftar versi tidak dapat dimuat.',
  noVersions: 'Belum ada versi yang dapat dipasang. Versi baru muncul di sini setelah dirilis tim pengembang.',
  downgradeWarn: (n: string, version: string) => `${n} reader memakai versi yang lebih baru dari ${v(version)}. Memasang versi lama hanya untuk keadaan darurat.`,
  downgradeAck: 'Saya mengerti, pasang versi lama',
  stepTarget: 'Reader tujuan',
  targetSelected: (n: string) => `Reader terpilih (${n})`,
  targetSelectedSub: 'Dari daftar reader',
  targetMatching: (n: string) => `Semua hasil filter (${n})`,
  targetRegion: 'Wilayah',
  targetRegionSub: 'Satu provinsi atau kabupaten/kota',
  targetVersion: 'Versi tertentu',
  targetVersionSub: 'Reader yang memakai versi tertentu',
  targetAll: 'Semua reader',
  targetAllSub: 'Seluruh Indonesia',
  pickProvince: 'Provinsi',
  pickProvincePlaceholder: 'Pilih provinsi',
  pickRegency: 'Kabupaten/kota',
  pickRegencyAll: 'Seluruh provinsi',
  pickRunning: 'Reader yang memakai',
  pickRunningPlaceholder: 'Pilih versi',
  stepStage: 'Tahap',
  stageCanary: '1 reader uji',
  stageCanarySub: 'Pastikan normal dulu, lalu lanjutkan ke yang lain',
  stagePercent: 'Sebagian',
  stagePercentSub: 'Urutan reader tetap untuk versi ini',
  stagePercentNote: 'Tahap berikutnya menambah reader, tidak mengulang yang sudah dipasang: setelah 5%, memilih 10% menambah 5% berikutnya.',
  reinstallNote: (name: string, version: string) => `${name} dipasangi ${v(version)} lagi; percobaannya dimulai dari awal.`,
  stageAll: 'Semua sekaligus',
  stageAllSub: 'Semua reader tujuan',
  percentLabel: 'Persentase reader',
  canaryPick: 'Reader uji',
  canaryPlaceholder: 'Pilih reader yang online',
  canaryLoading: 'Mencari reader online…',
  canaryNone: 'Tidak ada reader online di tujuan ini. Pilih tahap lain atau tunggu reader tersambung.',
  stepSchedule: 'Waktu pemasangan',
  scheduleWindow: (start: string, end: string, tz: string) => `Jam pemeliharaan, ${start}–${end} ${tz}`,
  scheduleWindowSub: 'Disarankan: reader berhenti membaca sekitar 1 menit saat dipasang',
  scheduleNow: 'Sekarang',
  scheduleNowSub: 'Dikirim pada pemeriksaan berikutnya',
  noWindow: 'Sistem tidak mengatur jam pemeliharaan, jadi pemasangan dikirim segera.',
  stepSummary: 'Ringkasan',
  previewLoading: 'Menghitung reader…',
  previewFailed: 'Ringkasan tidak dapat dihitung.',
  previewIncomplete: 'Lengkapi pilihan di atas untuk melihat ringkasan.',
  previewLine: (n: string, version: string) => `${n} reader akan dipasang ${v(version)}`,
  previewOffline: (n: string) => `${n} offline`,
  previewDowngrade: (n: string) => `${n} turun versi`,
  previewInstalled: (n: string) => `${n} sudah memakai versi ini dan dilewati`,
  previewNone: 'Tidak ada reader yang perlu dipasang: semuanya sudah memakai atau dijadwalkan versi ini.',
  previewSample: 'Contoh',
  previewMore: (n: string) => `dan ${n} lainnya`,
  offlineNote: 'Reader yang offline dipasang begitu tersambung lagi.',
  confirm: (n: string) => `Pasang ke ${n} reader`,
  confirmIdle: 'Pasang',
  bigTitle: (n: string, version: string) => `Pasang ${v(version)} ke ${n} reader sekaligus?`,
  bigBody: 'Semua reader tujuan dipasang tanpa tahap uji. Sebaiknya coba di 1 reader atau sebagian dulu.',
  bigLabel: 'Ya, pasang semua',
  bigNow: 'Pemasangan dikirim sekarang, di luar jam pemeliharaan.',
  downgradeBlocked: 'Centang persetujuan turun versi di atas untuk melanjutkan.',
  downgradeStep: (n: string) => `${n} reader akan turun versi.`,
  done: (n: string, version: string, immediate: boolean) => `${v(version)} dijadwalkan untuk ${n} reader${immediate ? '' : ' pada jam pemeliharaan'}`,
  downgradeRefused: 'Sebagian reader memakai versi yang lebih baru. Centang persetujuan turun versi untuk melanjutkan.',
} as const;

export const OTA_CONFIRM = {
  retryTitle: (version: string) => `Ulangi pemasangan ${v(version)}?`,
  retryBody: (n: string) => `${n} reader yang gagal atau berhenti akan mencoba lagi dari awal. Reader yang kembali ke versi lama tidak ikut.`,
  retryLabel: 'Ulangi',
  retried: (n: string) => `${n} reader mencoba lagi`,
  retriedNone: 'Tidak ada reader yang perlu diulang',
  cancelTitle: (what: string) => `Batalkan pembaruan ${what}?`,
  cancelPendingWhat: (n: string) => `${n} reader yang menunggu`,
  cancelSelectedWhat: (n: string) => `${n} reader terpilih`,
  cancelMatchingWhat: (n: string) => `semua ${n} reader hasil filter`,
  cancelBody: 'Hanya pembaruan yang belum terpasang yang dibatalkan; reader yang sudah memakai versi baru tidak berubah. Reader yang sedang memasang tetap menyelesaikannya, atau kembali sendiri ke versi lama bila gagal.',
  cancelLabel: 'Batalkan pembaruan',
  cancelled: (n: string) => `Pembaruan ${n} reader dibatalkan`,
  cancelledNone: 'Tidak ada pembaruan yang menunggu',
  deleteTitle: (version: string) => `Hapus ${v(version)}?`,
  deleteBody: 'Versi ini dihapus dari sistem dan tidak bisa dipasang lagi. Reader yang sudah memakainya tidak terpengaruh.',
  deleteLabel: 'Hapus versi',
  deleted: (version: string) => `${v(version)} dihapus`,
  inUse: 'Versi ini masih dijadwalkan untuk reader. Batalkan pembaruannya dulu, lalu hapus.',
} as const;
